/**
=========================================================
* Material Dashboard 2 PRO React TS - v1.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import Grid from '@mui/material/Grid';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';

// Material Dashboard 2 PRO React TS components
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDAlert from 'components/MDAlert';

// NewUser page components
import FormField from '../FormField';
import { Checkbox } from '@mui/material';
import { useUser } from 'context/user';
import { useState, useEffect } from 'react';
import { PRICE_PER_TOKEN } from 'config';

function UserInfo({ formData }: any): JSX.Element {
  const { formField, values, errors, touched, handleChange } = formData;
  const {
    fullName,
    email,
    password,
    repeatPassword,
    isActive,
    subscriptionDuration,
    allowCustomPlaylists,
    playlist,
  } = formField;
  const {
    fullName: firstNameV,
    email: emailV,
    password: passwordV,
    repeatPassword: repeatPasswordV,
    isActive: isActiveV,
    subscriptionDuration: subscriptionDurationV,
    allowCustomPlaylists: allowCustomPlaylistsV,
    playlist: playlistV,
  } = values;

  const { user } = useUser();
  const [creditCost, setCreditCost] = useState(0);
  const [hasEnoughCredits, setHasEnoughCredits] = useState(true);

  // Calculate credit cost based on subscription duration
  useEffect(() => {
    if (subscriptionDurationV) {
      const priceModifier =
        user.priceModifiers[
          subscriptionDurationV as keyof typeof user.priceModifiers
        ] ?? 1;

      const cost =
        parseInt(subscriptionDurationV) * PRICE_PER_TOKEN * priceModifier;
      setCreditCost(cost);
      setHasEnoughCredits(user.credits >= cost);
    }
  }, [subscriptionDurationV, user]);

  const handleSubscriptionChange = (event: SelectChangeEvent) => {
    handleChange({
      target: {
        name: subscriptionDuration.name,
        value: event.target.value,
      },
    });
  };

  return (
    <MDBox>
      <MDBox lineHeight={0} mb={2}>
        <MDTypography variant="h5">Create new account</MDTypography>
      </MDBox>
      <MDBox mt={1.625}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <FormField
              type={fullName.type}
              label={fullName.label}
              name={fullName.name}
              value={firstNameV}
              placeholder={fullName.placeholder}
              error={errors.firstName && touched.firstName}
              success={firstNameV.length > 0 && !errors.firstName}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormField
              type={email.type}
              label={email.label}
              name={email.name}
              value={emailV}
              placeholder={email.placeholder}
              error={errors.email && touched.email}
              success={emailV.length > 0 && !errors.email}
            />
          </Grid>
        </Grid>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <FormField
              type={password.type}
              label={password.label}
              name={password.name}
              value={passwordV}
              placeholder={password.placeholder}
              error={errors.password && touched.password}
              success={passwordV.length > 0 && !errors.password}
              inputProps={{ autoComplete: '' }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormField
              type={repeatPassword.type}
              label={repeatPassword.label}
              name={repeatPassword.name}
              value={repeatPasswordV}
              placeholder={repeatPassword.placeholder}
              error={errors.repeatPassword && touched.repeatPassword}
              success={repeatPasswordV.length > 0 && !errors.repeatPassword}
              inputProps={{ autoComplete: '' }}
            />
          </Grid>
        </Grid>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} spacing={3}>
            <FormField
              type={playlist.type}
              label={playlist.label}
              name={playlist.name}
              value={playlistV}
              placeholder={playlist.placeholder}
              error={
                (errors.playlist && touched.playlist) ||
                (!allowCustomPlaylistsV && playlistV.length === 0)
              }
              inputProps={{ autoComplete: '' }}
            />
          </Grid>
        </Grid>
        <Grid container spacing={3} mt={1}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel id="subscription-duration-label">
                {subscriptionDuration.label}
              </InputLabel>
              <Select
                labelId="subscription-duration-label"
                id="subscription-duration"
                value={subscriptionDurationV}
                label={subscriptionDuration.label}
                name={subscriptionDuration.name}
                onChange={handleSubscriptionChange}
              >
                {subscriptionDuration.options.map((option: any) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <MDBox
              p={2}
              bgcolor="background.neutral"
              borderRadius="1"
              sx={{ backgroundColor: '#f8f9fa' }}
            >
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <MDTypography variant="body2" fontWeight="bold">
                    Cost:
                  </MDTypography>
                </Grid>
                <Grid item xs={6}>
                  <MDTypography variant="body2">
                    {creditCost.toFixed(2)} credits
                  </MDTypography>
                </Grid>

                {user?.priceModifiers[
                  subscriptionDurationV as keyof typeof user.priceModifiers
                ] &&
                  user?.priceModifiers[
                    subscriptionDurationV as keyof typeof user.priceModifiers
                  ] !== 1 && (
                    <Grid item xs={6}>
                      <MDTypography variant="body2" fontWeight="bold">
                        Save:
                      </MDTypography>
                    </Grid>
                  )}
                {user?.priceModifiers[
                  subscriptionDurationV as keyof typeof user.priceModifiers
                ] &&
                  user?.priceModifiers[
                    subscriptionDurationV as keyof typeof user.priceModifiers
                  ] !== 1 && (
                    <Grid item xs={6}>
                      <MDTypography variant="body2">
                        {(
                          PRICE_PER_TOKEN * subscriptionDurationV -
                          PRICE_PER_TOKEN *
                            user?.priceModifiers[
                              subscriptionDurationV as keyof typeof user.priceModifiers
                            ] *
                            subscriptionDurationV
                        ).toFixed(2)}{' '}
                        credits
                      </MDTypography>
                    </Grid>
                  )}
                <Grid item xs={6}>
                  <MDTypography variant="body2" fontWeight="bold">
                    Your Balance:
                  </MDTypography>
                </Grid>
                <Grid item xs={6}>
                  <MDTypography
                    variant="body2"
                    color={hasEnoughCredits ? 'success' : 'error'}
                  >
                    {user?.credits || 0} credits
                  </MDTypography>
                </Grid>
              </Grid>
            </MDBox>
            {!hasEnoughCredits && (
              <MDAlert color="error" mt={2}>
                Insufficient credits for this subscription duration
              </MDAlert>
            )}
          </Grid>
        </Grid>
        <Grid container spacing={3} mt={1}>
          <Grid item xs={12} sm={3}>
            <MDTypography
              variant="button"
              fontWeight="regular"
              color="text"
              sx={{ cursor: 'pointer', userSelect: 'none', ml: -1 }}
            >
              &nbsp;&nbsp;Active&nbsp;
            </MDTypography>
            <Checkbox
              name={isActive.name}
              checked={isActiveV}
              value={isActiveV}
              onClick={handleChange}
            />
          </Grid>
        </Grid>
        <Grid item xs={12} sm={4}>
          <MDTypography
            variant="button"
            fontWeight="regular"
            color="text"
            sx={{ cursor: 'pointer', userSelect: 'none', ml: -1 }}
          >
            &nbsp;&nbsp;Allow custom playlists&nbsp;
          </MDTypography>
          <Checkbox
            name={allowCustomPlaylists.name}
            checked={allowCustomPlaylistsV}
            value={allowCustomPlaylistsV}
            onClick={handleChange}
          />
        </Grid>
      </MDBox>
    </MDBox>
  );
}

export default UserInfo;
